import { createContext, memo, useContext, useRef, type CSSProperties } from 'react';

import type { StorefrontItem } from '../api/types.ts';
import { packSizeDetail } from '../core/pack-size.ts';
import { useStore } from '../core/store.ts';
import { useStrings } from '../i18n/index.ts';
import { canAdd, cartStore, decreaseInCart, qtyOf } from '../state/cart.ts';
import { addWithChecks } from '../state/cart-sync.ts';
import { launchCartFlight } from '../state/cart-flight.ts';
import { openCart, openSignIn, pushRoute } from '../state/router.ts';
import { sessionStore } from '../state/session.ts';
import { favoritesStore, toggleFavorite } from '../state/stores.ts';
import { Icon } from './icons.tsx';
import { AddButton, cx, PriceLabel, QuantityStepper, RemoteImage, SaleBadge, Skeleton } from './kit.tsx';

/**
 * Куда ведут карточка товара и её «В корзину». На вкладке это экран стека и лист корзины;
 * лист корзины подменяет оба перехода своими шагами.
 */
export type ProductNav = { open: (item: StorefrontItem) => void; goToCart: () => void };

export const ProductNavContext = createContext<ProductNav>({
  open: (item) => pushRoute({ name: 'product', item }),
  goToCart: openCart,
});

/** Сердце избранного на плитке и в карточке товара. Гостю поднимает лист входа. */
export function FavoriteButton({ productId, size = 36 }: { productId: string; size?: number }) {
  const strings = useStrings();
  const isFavorite = useStore(favoritesStore).has(productId);
  return (
    <button
      type="button"
      className={cx('favorite-button', isFavorite && 'is-on')}
      style={{ '--size': `${size}px` } as CSSProperties}
      aria-label={isFavorite ? strings.a11yRemoveFavorite : strings.a11yAddFavorite}
      aria-pressed={isFavorite}
      onClick={(event) => {
        event.stopPropagation();
        if (sessionStore.get().profile === null) openSignIn();
        else toggleFavorite(productId);
      }}
    >
      <Icon name={isFavorite ? 'heartFill' : 'heart'} size={size * 0.5} weight={2.2} />
    </button>
  );
}

/**
 * Плитка товара: фото на светлой подложке, круглый «плюс» в углу, бейдж скидки, сердце
 * избранного, под ними цена и название. В корзине «плюс» превращается в степпер. Тап по
 * фото или тексту открывает карточку товара.
 */
export const ProductTile = memo(function ProductTile({ item, onOpen }: { item: StorefrontItem; onOpen?: (item: StorefrontItem) => void }) {
  const strings = useStrings();
  const cart = useStore(cartStore);
  const quantity = qtyOf(cart, item.id);
  const photo = useRef<HTMLDivElement>(null);
  const nav = useContext(ProductNavContext);
  const open = () => (onOpen ? onOpen(item) : nav.open(item));

  // Только пометка об остатке; фасовка видна в степпере, когда товар в корзине.
  const stockNote = item.availableQty <= 0 ? strings.outOfStock : item.availableQty <= 5 ? strings.onlyLeft(item.availableQty) : null;

  return (
    <article className={cx('tile', item.availableQty <= 0 && 'is-out')}>
      <div className="tile-photo" ref={photo} onClick={open}>
        <RemoteImage src={item.photoUrl} />
        <div className="tile-badges">
          {item.isNew && <span className="tag-badge">{strings.badgeNew}</span>}
          {item.ageRestricted && <span className="tag-badge neutral">{strings.badgeAge}</span>}
          {/* Пока в углу степпер, бейдж скидки уходит наверх, чтобы не столкнуться с ним. */}
          {quantity > 0 && item.compareAtPriceRsd && <SaleBadge price={item.priceRsd} compareAt={item.compareAtPriceRsd} />}
        </div>
        <div className="tile-favorite">
          <FavoriteButton productId={item.id} size={30} />
        </div>
        {quantity === 0 && item.compareAtPriceRsd && (
          <div className="tile-sale">
            <SaleBadge price={item.priceRsd} compareAt={item.compareAtPriceRsd} />
          </div>
        )}
        <div className="tile-control">
          {quantity > 0 ? (
            <QuantityStepper
              compact
              quantity={quantity}
              canIncrease={canAdd(cart, item.id) && quantity < item.availableQty}
              detail={packSizeDetail(item.sizeLabel)}
              onDecrease={() => decreaseInCart(item.id)}
              onIncrease={() => addWithChecks(item)}
            />
          ) : (
            <AddButton
              enabled={item.availableQty > 0}
              size={36}
              onClick={() => {
                if (addWithChecks(item)) launchCartFlight(item.photoUrl, photo.current);
              }}
            />
          )}
        </div>
      </div>
      <button type="button" className="tile-caption" onClick={open}>
        <PriceLabel price={item.priceRsd} compareAt={item.compareAtPriceRsd} />
        <span className="tile-name">{item.name}</span>
        {stockNote && <span className={cx('tile-stock', item.availableQty > 0 && 'is-low')}>{stockNote}</span>}
      </button>
    </article>
  );
});

/** Горизонтальная лента плиток товаров. */
export function ProductLane({ items, pad }: { items: StorefrontItem[]; pad?: number }) {
  return (
    <div className="lane snap product-lane" style={pad === undefined ? undefined : ({ '--lane-pad': `${pad}px` } as CSSProperties)}>
      {items.map((item) => (
        <ProductTile key={item.id} item={item} />
      ))}
    </div>
  );
}

/**
 * Сетка плиток: крупные в категории, поиске, подборке и избранном, плотные для покупок на
 * главной. Число колонок следует из ширины.
 */
export function ProductGrid({ items, dense }: { items: StorefrontItem[]; dense?: boolean }) {
  return (
    <div className={cx('product-grid', dense && 'is-dense')}>
      {items.map((item) => (
        <ProductTile key={item.id} item={item} />
      ))}
    </div>
  );
}

export function ProductGridSkeleton({ count = 6 }: { count?: number }) {
  return (
    <div className="product-grid" aria-hidden="true">
      {Array.from({ length: count }, (_, index) => (
        <div key={index} className="stack" style={{ gap: 8 }}>
          <Skeleton style={{ aspectRatio: '1' }} />
          <Skeleton width={70} height={16} radius={6} />
          <Skeleton height={14} radius={6} />
        </div>
      ))}
    </div>
  );
}
